import React, { useMemo } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import {
  ChefHat,
  Users,
  BookOpen,
  Shield,
  Leaf,
  Globe,
  Sparkles,
  CheckCircle,
} from "lucide-react";

const OurVision = () => {
  // SEO Data
  const seoData = useMemo(
    () => ({
      title:
        "Our Vision | Building a Digestive-Friendly Food Community | GentleGourmet",
      description:
        "Discover GentleGourmet's vision for a world where digestive conditions don't limit food enjoyment. Join our community-driven approach to gentle, delicious cooking for GERD, IBS, and gastritis.",
      keywords:
        "GentleGourmet vision, digestive health community, GERD support, IBS community, gastritis recipes, gentle cooking, food community",
      canonicalUrl: "https://gentlegourmet.app/pages/our-vision",
      ogImage: "https://gentlegourmet.app/images/og-vision.jpg",
    }),
    []
  );

  // JSON-LD Structured Data
  const jsonLdAboutPage = useMemo(
    () => ({
      "@context": "https://schema.org",
      "@type": "AboutPage",
      name: "Our Vision - GentleGourmet",
      description: seoData.description,
      url: seoData.canonicalUrl,
      mainEntity: {
        "@type": "Organization",
        name: "GentleGourmet",
        url: "https://gentlegourmet.app",
        description:
          "A community-built platform for GERD, gastritis, and IBS-friendly cooking.",
        foundingDate: "2023",
        slogan: "Food that's gentle on your stomach—and generous to your taste buds",
      },
    }),
    [seoData]
  );

  const jsonLdBreadcrumbs = useMemo(
    () => ({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Home",
          item: "https://gentlegourmet.app/",
        },
        {
          "@type": "ListItem",
          position: 2,
          name: "Our Vision",
          item: seoData.canonicalUrl,
        },
      ],
    }),
    [seoData]
  );

  return (
    <main className="bg-gradient-to-b from-amber-50/40 via-white to-white">
      {/* SEO Head Tags */}
      <Helmet>
        <html lang="en" />
        <title>{seoData.title}</title>
        <meta name="description" content={seoData.description} />
        <meta name="keywords" content={seoData.keywords} />
        <link rel="canonical" href={seoData.canonicalUrl} />
        <meta name="robots" content="index, follow, max-image-preview:large" />
        <meta property="og:title" content={seoData.title} />
        <meta property="og:description" content={seoData.description} />
        <meta property="og:url" content={seoData.canonicalUrl} />
        <meta property="og:image" content={seoData.ogImage} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="GentleGourmet" />
        <meta property="og:locale" content="en_US" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seoData.title} />
        <meta name="twitter:description" content={seoData.description} />
        <meta name="twitter:image" content={seoData.ogImage} />
        <script type="application/ld+json">
          {JSON.stringify(jsonLdAboutPage)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(jsonLdBreadcrumbs)}
        </script>
      </Helmet>
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div
          aria-hidden
          className="pointer-events-none absolute -top-40 right-[-10%] h-96 w-96 rounded-full bg-amber-200/40 blur-3xl"
        />
        <div
          aria-hidden
          className="pointer-events-none absolute -left-24 top-20 h-64 w-64 rounded-full bg-rose-200/40 blur-3xl"
        />
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-14 sm:py-20">
          <div className="mx-auto max-w-3xl text-center">
            <span className="inline-flex items-center gap-2 rounded-full border border-amber-200 bg-white/70 px-3 py-1 text-xs font-semibold text-amber-700 shadow-sm backdrop-blur">
              <Sparkles className="h-3.5 w-3.5" />
              Our Vision
            </span>
            <h1 className="mt-4 text-3xl font-extrabold tracking-tight text-slate-900 sm:text-5xl">
              Food that’s gentle on your stomach—and generous to your taste buds
            </h1>
            <p className="mt-4 text-base leading-relaxed text-slate-600 sm:text-lg">
              GentleGourmet is a community-built home for GERD, gastritis, and
              IBS-friendly cooking. We help you discover, cook, and share dishes
              that feel good <em>and</em> taste great—so you can love food again
              without the flare-ups.
            </p>
            <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
              <Link
                reloadDocument
                to="/"
                className="inline-flex items-center justify-center rounded-full bg-amber-500 px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-amber-600 active:bg-amber-700"
              >
                Explore Recipes
              </Link>
              <Link
                to="/sign_up"
                className="inline-flex items-center justify-center rounded-full border border-slate-200 bg-white px-5 py-2.5 text-sm font-semibold text-slate-700 shadow-sm hover:border-slate-300 hover:bg-slate-50"
              >
                Join the Community
              </Link>
            </div>
          </div>

          {/* Stats band */}
          <div className="mx-auto mt-10 grid max-w-4xl grid-cols-3 gap-4 rounded-2xl border border-slate-200 bg-white p-4 text-center shadow-sm sm:gap-6 sm:p-6">
            <div>
              <div className="text-2xl font-extrabold text-amber-600 sm:text-3xl">
                2M+
              </div>
              <div className="text-xs font-medium text-slate-500">
                Recipe Matches
              </div>
            </div>
            <div className="border-l border-slate-200" />
            <div>
              <div className="text-2xl font-extrabold text-amber-600 sm:text-3xl">
                350k+
              </div>
              <div className="text-xs font-medium text-slate-500">
                Community Members
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission pillars */}
      <section className="py-12 sm:py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <header className="mx-auto mb-8 max-w-3xl text-center sm:mb-12">
            <h2 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
              What we stand for
            </h2>
            <p className="mt-3 text-slate-600">
              Our north star is simple: make it easy to cook the food your body
              loves—without sacrificing joy, flavor, or community.
            </p>
          </header>

          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {/* Supportive Community */}
            <article className="group rounded-2xl border border-slate-200 bg-white p-6 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-rose-50 text-rose-600">
                <Users className="h-5 w-5" />
              </div>
              <h3 className="mt-4 text-lg font-semibold text-slate-900">
                Supportive Community
              </h3>
              <ul className="mt-3 space-y-2 text-sm text-slate-600">
                <li className="flex gap-2">
                  <CheckCircle className="mt-0.5 h-4 w-4 text-emerald-600" />
                  Safe, welcoming space for sensitive diets
                </li>
                <li className="flex gap-2">
                  <CheckCircle className="mt-0.5 h-4 w-4 text-emerald-600" />
                  Share experiences, tips, and substitutions
                </li>
                <li className="flex gap-2">
                  <CheckCircle className="mt-0.5 h-4 w-4 text-emerald-600" />
                  Celebrate wins—from symptom relief to great dinners
                </li>
              </ul>
            </article>

            {/* Comprehensive Recipes */}
            <article className="group rounded-2xl border border-slate-200 bg-white p-6 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-50 text-amber-600">
                <BookOpen className="h-5 w-5" />
              </div>
              <h3 className="mt-4 text-lg font-semibold text-slate-900">
                Comprehensive Recipes
              </h3>
              <ul className="mt-3 space-y-2 text-sm text-slate-600">
                <li className="flex gap-2">
                  <CheckCircle className="mt-0.5 h-4 w-4 text-emerald-600" />
                  Searchable library for GERD/gastritis/IBS needs
                </li>
                <li className="flex gap-2">
                  <CheckCircle className="mt-0.5 h-4 w-4 text-emerald-600" />
                  Ingredient notes, prep details, nutrition insights
                </li>
                <li className="flex gap-2">
                  <CheckCircle className="mt-0.5 h-4 w-4 text-emerald-600" />
                  Community-curated and constantly improving
                </li>
              </ul>
            </article>

            {/* Accessible & Inclusive */}
            <article className="group rounded-2xl border border-slate-200 bg-white p-6 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600">
                <Globe className="h-5 w-5" />
              </div>
              <h3 className="mt-4 text-lg font-semibold text-slate-900">
                Accessible & Inclusive
              </h3>
              <ul className="mt-3 space-y-2 text-sm text-slate-600">
                <li className="flex gap-2">
                  <CheckCircle className="mt-0.5 h-4 w-4 text-emerald-600" />
                  Clear UI, responsive on any device
                </li>
                <li className="flex gap-2">
                  <CheckCircle className="mt-0.5 h-4 w-4 text-emerald-600" />
                  Flexible filters for personal triggers & cuisines
                </li>
                <li className="flex gap-2">
                  <CheckCircle className="mt-0.5 h-4 w-4 text-emerald-600" />
                  Built to welcome global voices & tastes
                </li>
              </ul>
            </article>

            {/* Education & Awareness */}
            <article className="group rounded-2xl border border-slate-200 bg-white p-6 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-sky-50 text-sky-600">
                <Shield className="h-5 w-5" />
              </div>
              <h3 className="mt-4 text-lg font-semibold text-slate-900">
                Education & Awareness
              </h3>
              <ul className="mt-3 space-y-2 text-sm text-slate-600">
                <li className="flex gap-2">
                  <CheckCircle className="mt-0.5 h-4 w-4 text-emerald-600" />
                  Practical guides on triggers & cooking methods
                </li>
                <li className="flex gap-2">
                  <CheckCircle className="mt-0.5 h-4 w-4 text-emerald-600" />
                  Expert insights from dietitians & clinicians
                </li>
                <li className="flex gap-2">
                  <CheckCircle className="mt-0.5 h-4 w-4 text-emerald-600" />
                  Empowerment through reliable, friendly info
                </li>
              </ul>
            </article>
          </div>
        </div>
      </section>

      {/* How we help (3-step) */}
      <section className="py-8 sm:py-14">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid items-center gap-8 lg:grid-cols-2">
            <div className="order-2 lg:order-1">
              <h2 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
                How GentleGourmet helps you thrive
              </h2>
              <ol className="mt-6 space-y-5">
                <li className="flex gap-3">
                  <div className="mt-0.5 h-7 w-7 shrink-0 rounded-full bg-amber-500 text-white flex items-center justify-center text-sm font-bold">
                    1
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900">
                      Discover recipes that fit your body
                    </h3>
                    <p className="mt-1 text-sm text-slate-600">
                      Use filters for GERD, gastritis, IBS, spice level, and
                      more—then save favorites for quick weekday wins.
                    </p>
                  </div>
                </li>
                <li className="flex gap-3">
                  <div className="mt-0.5 h-7 w-7 shrink-0 rounded-full bg-amber-500 text-white flex items-center justify-center text-sm font-bold">
                    2
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900">
                      Cook with confidence
                    </h3>
                    <p className="mt-1 text-sm text-slate-600">
                      Clear steps, ingredient notes, and substitution ideas keep
                      flavor high and symptoms low.
                    </p>
                  </div>
                </li>
                <li className="flex gap-3">
                  <div className="mt-0.5 h-7 w-7 shrink-0 rounded-full bg-amber-500 text-white flex items-center justify-center text-sm font-bold">
                    3
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900">
                      Share & grow together
                    </h3>
                    <p className="mt-1 text-sm text-slate-600">
                      Post your tweaks, review recipes, and swap tips—your
                      experience helps the next person eat better.
                    </p>
                  </div>
                </li>
              </ol>

              <div className="mt-8 flex flex-wrap gap-3">
                <Link
                  reloadDocument
                  to="/"
                  className="inline-flex items-center justify-center rounded-full border border-slate-200 bg-white px-5 py-2.5 text-sm font-semibold text-slate-700 shadow-sm hover:border-slate-300 hover:bg-slate-50"
                >
                  Browse Meal Plans
                </Link>
                <Link
                  reloadDocument
                  to="/new_post"
                  className="inline-flex items-center justify-center rounded-full bg-emerald-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-emerald-700 active:bg-emerald-800"
                >
                  Share a Recipe
                </Link>
              </div>
            </div>

            <div className="order-1 lg:order-2">
              <div className="relative mx-auto max-w-lg overflow-hidden rounded-3xl border border-slate-200 bg-white p-6 shadow-md">
                <div className="grid grid-cols-3 gap-3 sm:gap-4">
                  <div className="rounded-2xl bg-rose-50 p-4 text-rose-700">
                    <ChefHat className="h-6 w-6" />
                    <p className="mt-2 text-sm font-semibold">
                      Gentle Techniques
                    </p>
                    <p className="text-xs text-rose-700/80">
                      Bake, steam, poach—delicious, low-irritation methods.
                    </p>
                  </div>
                  <div className="rounded-2xl bg-amber-50 p-4 text-amber-700">
                    <Leaf className="h-6 w-6" />
                    <p className="mt-2 text-sm font-semibold">Trigger-Smart</p>
                    <p className="text-xs text-amber-700/80">
                      Notes on acidity, spice, fat, and portion size.
                    </p>
                  </div>
                  <div className="rounded-2xl bg-emerald-50 p-4 text-emerald-700">
                    <Users className="h-6 w-6" />
                    <p className="mt-2 text-sm font-semibold">
                      Community-Tried
                    </p>
                    <p className="text-xs text-emerald-700/80">
                      Tips & swaps from real cooks who’ve been there.
                    </p>
                  </div>
                  <div className="col-span-3 rounded-2xl bg-slate-50 p-4 text-slate-700">
                    <p className="text-sm font-semibold">
                      “Our goal is simple: help you love food again.” —
                      GentleGourmet
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Closing statement */}
      <section className="pb-16 sm:pb-24">
        <div className="mx-auto max-w-5xl px-4 text-center sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
            A kinder way to cook
          </h2>
          <p className="mx-auto mt-4 max-w-3xl text-slate-600">
            By uniting people managing GERD, gastritis, and IBS through shared
            recipes and knowledge, we’re building a practical, hopeful path back
            to joyful eating. Together, we can make cooking feel safe, simple,
            and genuinely delicious.
          </p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
            <Link
              reloadDocument
              to="/"
              className="inline-flex items-center justify-center rounded-full bg-amber-500 px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-amber-600 active:bg-amber-700"
            >
              Start Cooking
            </Link>
            <Link
              reloadDocument
              to="/pages/contact"
              className="inline-flex items-center justify-center rounded-full border border-slate-200 bg-white px-5 py-2.5 text-sm font-semibold text-slate-700 shadow-sm hover:border-slate-300 hover:bg-slate-50"
            >
              Contact Us
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default OurVision;
